/**
 * Product lightbox: Swiper slides + transform zoom (desktop).
 */
class ProductGalleryLightbox {
  constructor(gallery) {
    this.gallery = gallery
    this.dialog = gallery.querySelector("[data-product-gallery-lightbox]")
    this.stage = gallery.querySelector("[data-lightbox-stage]")
    this.viewport = gallery.querySelector("[data-lightbox-viewport]")
    this.counter = gallery.querySelector("[data-lightbox-counter]")
    this.thumbs = gallery.querySelectorAll("[data-lightbox-thumb]")
    this.lightboxSwiperComponent = gallery.querySelector("#swiper-lightbox")

    this.imageSwiper = null
    this.lightboxSwiper = null
    this.currentIndex = 0
    this._syncing = false
    this._pageScrollLocked = false

    this._zoomScale = 2
    this._zoomState = "idle"
    this._zoomRatioX = 0.5
    this._zoomRatioY = 0.5
    this._zoomBaseW = 0
    this._zoomBaseH = 0

    this._panX = 0
    this._panY = 0
    this._panPointerId = null
    this._panStartX = 0
    this._panStartY = 0
    this._panOriginX = 0
    this._panOriginY = 0

    this._pointerStartX = 0
    this._pointerStartY = 0
    this._pointerId = null
    this._didSwipe = false
    this._swipeThreshold = 50
    this._tapThreshold = 8

    this._onLightboxSwiperReady = this._onLightboxSwiperReady.bind(this)
    this._onTransformTransitionEnd = this._onTransformTransitionEnd.bind(this)
  }

  init() {
    if (!this.dialog) return
    this.bindDialog()
    this.bindBlankClose()
    this.bindFrameZoom()
    document.addEventListener("swiper-component:ready", this._onLightboxSwiperReady)
    if (this.lightboxSwiperComponent?.swiper) {
      this.attachLightboxSwiper(this.lightboxSwiperComponent.swiper)
    }
  }

  destroy() {
    document.removeEventListener("swiper-component:ready", this._onLightboxSwiperReady)
    this.unlockPageScroll()
  }

  setImageSwiper(swiper) {
    this.imageSwiper = swiper
    if (swiper && !this.currentIndex) {
      this.currentIndex = swiper.activeIndex
    }
  }

  _onLightboxSwiperReady(event) {
    const { swiperComponent, swiper } = event.detail
    if (swiperComponent?.id === "swiper-lightbox" && this.gallery.contains(swiperComponent)) {
      this.attachLightboxSwiper(swiper)
    }
  }

  attachLightboxSwiper(swiper) {
    if (this.lightboxSwiper === swiper) return
    this.lightboxSwiper = swiper
    this._configureSwiperDrag(swiper)
    swiper.on("slideChange", () => {
      if (this._syncing) return
      this.resetZoom()
      this.currentIndex = swiper.activeIndex
      this.syncUI(this.currentIndex)
      this._syncing = true
      if (this.imageSwiper) {
        if (typeof this.gallery.slideToMediaIndex === "function") {
          this.gallery.slideToMediaIndex(this.currentIndex)
        } else if (this.imageSwiper.activeIndex !== this.currentIndex) {
          this.imageSwiper.slideTo(this.currentIndex)
        }
      }
      this._syncing = false
    })
  }

  getVisibleIndexes() {
    const slides = this.lightboxSwiper?.slides
    if (!slides?.length) return []
    return Array.from(slides)
      .map((slide, index) => (slide.classList?.contains("is-gallery-hidden") ? -1 : index))
      .filter((index) => index >= 0)
  }

  get slideCount() {
    const visible = this.getVisibleIndexes()
    return visible.length || this.lightboxSwiper?.slides?.length || this.thumbs?.length || 0
  }

  goToAdjacent(delta) {
    const visible = this.getVisibleIndexes()
    if (!visible.length) return
    let pos = visible.indexOf(this.currentIndex)
    if (pos < 0) pos = 0
    const nextPos = (pos + delta + visible.length) % visible.length
    this.goToSlide(visible[nextPos])
  }

  bindDialog() {
    this.dialog.querySelectorAll("[data-lightbox-close]").forEach((btn) => {
      btn.addEventListener("click", () => this.close())
    })
    this.dialog.querySelector("[data-lightbox-prev]")?.addEventListener("click", () => {
      this.goToAdjacent(-1)
    })
    this.dialog.querySelector("[data-lightbox-next]")?.addEventListener("click", () => {
      this.goToAdjacent(1)
    })
    this.thumbs?.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        if (thumb.classList.contains("is-gallery-hidden")) return
        const index = parseInt(thumb.dataset.lightboxThumb, 10)
        if (!Number.isNaN(index)) this.goToSlide(index)
      })
    })
    this.dialog.addEventListener("cancel", (event) => {
      event.preventDefault()
      this.close()
    })
  }

  _interactiveSelector() {
    return "[data-lightbox-prev], [data-lightbox-next], [data-lightbox-close], [data-lightbox-thumb], .product-header-lightbox__footer, .product-header-lightbox__dots, .product-header-lightbox__dots .swiper-pagination-bullet, [data-lightbox-pagination]"
  }

  _isDesktop() {
    return window.matchMedia("(min-width: 768px)").matches
  }

  _configureSwiperDrag(swiper) {
    const desktop = this._isDesktop()
    swiper.params.simulateTouch = !desktop
    swiper.allowTouchMove = !desktop
    if (desktop) {
      swiper.params.noSwiping = true
      swiper.params.noSwipingSelector = "[data-lightbox-frame], .product-header-lightbox__image"
    } else {
      swiper.params.noSwiping = false
      swiper.params.noSwipingSelector = ""
    }
    this._configureMobilePagination(swiper)
  }

  _configureMobilePagination(swiper) {
    if (!swiper) return
    if (swiper.pagination?.el) {
      swiper.pagination.destroy()
      swiper.params.pagination = false
    }
  }

  _isPointOnActiveImage(x, y) {
    const img =
      this.getActiveFrame()?.querySelector(".product-header-lightbox__image") ??
      this.gallery.querySelector("#swiper-lightbox .swiper-slide-active .product-header-lightbox__image") ??
      this.gallery.querySelectorAll("#swiper-lightbox .product-header-lightbox__image")[this.currentIndex]
    if (!img) return false
    const rect = img.getBoundingClientRect()
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }

  bindBlankClose() {
    if (!this.stage) return
    const interactiveSelector = this._interactiveSelector()
    const lightboxTop = this.dialog.querySelector(".product-header-lightbox__top")

    this.stage.addEventListener("pointerdown", (event) => {
      if (this._zoomState !== "idle") return
      if (event.button !== 0) return
      if (event.target.closest(interactiveSelector)) return
      if (!this.stage.contains(event.target)) return
      this._pointerStartX = event.clientX
      this._pointerStartY = event.clientY
      this._pointerId = event.pointerId
      this._didSwipe = false
    })

    this.stage.addEventListener("pointerup", (event) => {
      if (this._zoomState !== "idle") return
      if (event.pointerId !== this._pointerId) return
      this._pointerId = null
      const deltaX = event.clientX - this._pointerStartX
      const deltaY = event.clientY - this._pointerStartY
      if (Math.abs(deltaX) < this._swipeThreshold || Math.abs(deltaX) < Math.abs(deltaY)) return
      this._didSwipe = true
      if (deltaX < 0) this.goToAdjacent(1)
      else this.goToAdjacent(-1)
    })

    this.stage.addEventListener("pointercancel", () => {
      this._pointerId = null
    })

    // Desktop only: tap empty stage / top chrome to close.
    // Mobile relies on the close button — blank-close races with the open tap (ghost click).
    this.stage.addEventListener("click", (event) => {
      if (!this._isDesktop()) return
      if (this._zoomState !== "idle") return
      if (this._didSwipe) {
        this._didSwipe = false
        return
      }
      if (event.target.closest(interactiveSelector)) return
      if (!this.stage.contains(event.target)) return
      if (this._isPointOnActiveImage(event.clientX, event.clientY)) return
      this.close()
    })

    lightboxTop?.addEventListener("click", (event) => {
      if (!this._isDesktop()) return
      if (this._zoomState !== "idle") return
      if (event.target.closest("[data-lightbox-close]")) return
      if (event.target === lightboxTop || event.target.closest("[data-lightbox-counter]")) {
        this.close()
      }
    })
  }

  bindFrameZoom() {
    this.gallery.addEventListener("pointerdown", (event) => {
      const frame = event.target.closest("[data-lightbox-frame]")
      if (!frame || !this.dialog.contains(frame)) return
      if (!this._isDesktop()) return
      if (event.button !== 0) return

      if (this._zoomState === "zoomed") {
        this._panPointerId = event.pointerId
        this._panStartX = event.clientX
        this._panStartY = event.clientY
        this._panOriginX = this._panX
        this._panOriginY = this._panY
        frame._tapPointerId = null
        event.stopPropagation()
        return
      }

      if (this._zoomState === "idle") {
        event.stopPropagation()
      }
      frame._tapStartX = event.clientX
      frame._tapStartY = event.clientY
      frame._tapPointerId = event.pointerId
    })

    this.gallery.addEventListener("pointermove", (event) => {
      if (this._zoomState !== "zoomed" || event.pointerId !== this._panPointerId) return
      const frame = this.getActiveFrame()
      if (!frame) return
      const deltaX = event.clientX - this._panStartX
      const deltaY = event.clientY - this._panStartY
      this._panX = this._panOriginX + deltaX
      this._panY = this._panOriginY + deltaY
      this._clampPan(frame)
      frame.style.transition = "none"
      this._applyFrameTransform(frame)
    })

    this.gallery.addEventListener("pointerup", (event) => {
      const frame = event.target.closest("[data-lightbox-frame]")
      if (!frame || !this.dialog.contains(frame)) return
      if (!this._isDesktop()) return

      if (this._zoomState === "zoomed" && event.pointerId === this._panPointerId) {
        this._panPointerId = null
        frame.style.transition = ""
        const deltaX = event.clientX - this._panStartX
        const deltaY = event.clientY - this._panStartY
        if (Math.abs(deltaX) <= this._tapThreshold && Math.abs(deltaY) <= this._tapThreshold) {
          event.stopPropagation()
          this.zoomOut(frame)
        }
        return
      }

      if (event.pointerId !== frame._tapPointerId) return
      frame._tapPointerId = null
      const deltaX = event.clientX - (frame._tapStartX ?? event.clientX)
      const deltaY = event.clientY - (frame._tapStartY ?? event.clientY)
      if (Math.abs(deltaX) > this._tapThreshold || Math.abs(deltaY) > this._tapThreshold) return
      event.stopPropagation()
      if (this._zoomState === "idle") {
        this.zoomIn(event, frame)
      }
    })

    this.gallery.addEventListener("pointercancel", (event) => {
      const frame = event.target.closest("[data-lightbox-frame]")
      if (frame) frame._tapPointerId = null
      if (event.pointerId === this._panPointerId) this._panPointerId = null
    })
  }

  getActiveFrame() {
    const slide = this.lightboxSwiper?.slides?.[this.currentIndex]
    if (slide) {
      return slide.querySelector("[data-lightbox-frame]")
    }
    return (
      this.gallery.querySelector("#swiper-lightbox .swiper-slide-active [data-lightbox-frame]") ??
      this.gallery.querySelectorAll("#swiper-lightbox [data-lightbox-frame]")[this.currentIndex] ??
      null
    )
  }

  lockPageScroll() {
    if (this._pageScrollLocked) return
    this._pageScrollLocked = true
    document.documentElement.classList.add("tw-overflow-hidden")
    document.body.classList.add("tw-overflow-hidden")
  }

  unlockPageScroll() {
    if (!this._pageScrollLocked) return
    this._pageScrollLocked = false
    document.documentElement.classList.remove("tw-overflow-hidden")
    document.body.classList.remove("tw-overflow-hidden")
  }

  open(index) {
    this.resetZoom()
    const visible = this.getVisibleIndexes()
    let target = index
    if (visible.length && !visible.includes(index)) {
      target = visible[0]
    }
    this.goToSlide(target, false)
    if (!this.dialog.open) {
      this.dialog.showModal()
      this.lockPageScroll()
    }
    if (this.lightboxSwiper) {
      this._configureMobilePagination(this.lightboxSwiper)
    }
    this.syncUI(target)
  }

  close() {
    this.resetZoom()
    if (this.dialog.open) {
      this.dialog.close()
    }
    this.unlockPageScroll()
  }

  goToSlide(index, syncMain = true) {
    const total = this.lightboxSwiper?.slides?.length ?? 0
    if (index < 0 || index >= total) return
    if (this.lightboxSwiper?.slides?.[index]?.classList?.contains("is-gallery-hidden")) return
    this.resetZoom()
    this.currentIndex = index
    this._syncing = true
    this.lightboxSwiper?.slideTo(index, 0)
    if (syncMain && this.imageSwiper) {
      if (typeof this.gallery.slideToMediaIndex === "function") {
        this.gallery.slideToMediaIndex(index)
      } else if (this.imageSwiper.activeIndex !== index) {
        this.imageSwiper.slideTo(index)
      }
    }
    this._syncing = false
    this.syncUI(index)
  }

  syncFromMainSwiper(index) {
    if (!this.dialog?.open || this._syncing) return
    this._syncing = true
    this.resetZoom()
    this.currentIndex = index
    this.lightboxSwiper?.slideTo(index, 0)
    this.syncUI(index)
    this._syncing = false
  }

  syncUI(index) {
    const visible = this.getVisibleIndexes()
    const visiblePos = visible.indexOf(index)
    const count = visible.length || this.slideCount || 1
    if (this.counter) {
      this.counter.textContent = `${(visiblePos >= 0 ? visiblePos : 0) + 1}/${count}`
    }
    this.thumbs?.forEach((thumb) => {
      const isActive = parseInt(thumb.dataset.lightboxThumb, 10) === index
      if (isActive) {
        thumb.setAttribute("aria-current", "true")
      } else {
        thumb.removeAttribute("aria-current")
      }
    })
    this._syncMobileDots(index)
  }

  _syncMobileDots(index) {
    if (this._isDesktop() || !this.dialog?.open) return
    const paginationEl = this.gallery.querySelector("[data-lightbox-pagination]")
    if (!paginationEl) return
    const visible = this.getVisibleIndexes()
    if (visible.length < 1) return

    let bullets = paginationEl.querySelectorAll(".swiper-pagination-bullet")
    if (bullets.length !== visible.length) {
      paginationEl.innerHTML = ""
      visible.forEach((domIndex, i) => {
        const bullet = document.createElement("button")
        bullet.type = "button"
        bullet.className = "swiper-pagination-bullet"
        bullet.setAttribute("aria-label", `Go to slide ${i + 1}`)
        bullet.addEventListener("click", () => this.goToSlide(domIndex))
        paginationEl.appendChild(bullet)
      })
      bullets = paginationEl.querySelectorAll(".swiper-pagination-bullet")
    }

    const activePos = visible.indexOf(index)
    bullets.forEach((bullet, i) => {
      bullet.classList.toggle("swiper-pagination-bullet-active", i === activePos)
    })
  }

  _setSwiperTouchEnabled(enabled) {
    if (!this.lightboxSwiper) return
    if (this._isDesktop()) {
      this.lightboxSwiper.allowTouchMove = false
      this.lightboxSwiper.params.simulateTouch = false
      return
    }
    this.lightboxSwiper.allowTouchMove = enabled
  }

  resetZoom() {
    const frame = this.getActiveFrame()
    if (frame) {
      frame.removeEventListener("transitionend", this._onTransformTransitionEnd)
      frame.classList.remove("is-zoomed", "is-zoom-layout")
      frame.style.transform = ""
      frame.style.transformOrigin = ""
      frame.style.width = ""
      frame.style.height = ""
      const img = frame.querySelector(".product-header-lightbox__image")
      if (img) {
        img.style.width = ""
        img.style.height = ""
        img.style.maxWidth = ""
        img.style.maxHeight = ""
        img.style.marginLeft = ""
        img.style.marginTop = ""
      }
    }
    this.viewport?.classList.remove("is-zoomed")
    this.stage?.classList.remove("is-zoomed-active")
    if (this.viewport) {
      this.viewport.scrollLeft = 0
      this.viewport.scrollTop = 0
    }
    this._zoomState = "idle"
    this._zoomBaseW = 0
    this._zoomBaseH = 0
    this._panX = 0
    this._panY = 0
    this._panPointerId = null
    this._setSwiperTouchEnabled(true)
  }

  _applyFrameTransform(frame) {
    const scale = this._zoomScale
    const tx = this._panX / scale
    const ty = this._panY / scale
    if (this._panX === 0 && this._panY === 0) {
      frame.style.transform = `scale(${scale})`
      return
    }
    frame.style.transform = `scale(${scale}) translate(${tx}px, ${ty}px)`
  }

  _clampPan(frame) {
    const img = frame.querySelector(".product-header-lightbox__image")
    if (!img || !this.viewport) return
    this._applyFrameTransform(frame)
    const imgRect = img.getBoundingClientRect()
    const vpRect = this.viewport.getBoundingClientRect()
    let adjustX = 0
    let adjustY = 0
    if (imgRect.width <= vpRect.width) {
      adjustX = vpRect.left + vpRect.width / 2 - (imgRect.left + imgRect.width / 2)
    } else {
      if (imgRect.left > vpRect.left) adjustX = vpRect.left - imgRect.left
      if (imgRect.right < vpRect.right) adjustX = vpRect.right - imgRect.right
    }
    if (imgRect.height <= vpRect.height) {
      adjustY = vpRect.top + vpRect.height / 2 - (imgRect.top + imgRect.height / 2)
    } else {
      if (imgRect.top > vpRect.top) adjustY = vpRect.top - imgRect.top
      if (imgRect.bottom < vpRect.bottom) adjustY = vpRect.bottom - imgRect.bottom
    }
    if (adjustX !== 0 || adjustY !== 0) {
      this._panX += adjustX
      this._panY += adjustY
      this._applyFrameTransform(frame)
    }
  }

  _pinZoomImageSize(img) {
    if (!img || !this._zoomBaseW || !this._zoomBaseH) return
    img.style.width = `${this._zoomBaseW}px`
    img.style.height = `${this._zoomBaseH}px`
    img.style.maxWidth = "none"
    img.style.maxHeight = "none"
  }

  zoomIn(event, frame) {
    if (!this.viewport || this._zoomState !== "idle") return
    const img = frame.querySelector(".product-header-lightbox__image")
    if (!img) return
    const rect = img.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    this._zoomRatioX = (event.clientX - rect.left) / rect.width
    this._zoomRatioY = (event.clientY - rect.top) / rect.height
    this._zoomBaseW = rect.width
    this._zoomBaseH = rect.height
    this._zoomState = "zooming"
    this._setSwiperTouchEnabled(false)

    this._pinZoomImageSize(img)
    frame.classList.add("is-zoomed")
    frame.style.transformOrigin = `${this._zoomRatioX * 100}% ${this._zoomRatioY * 100}%`
    frame.addEventListener("transitionend", this._onTransformTransitionEnd)
    requestAnimationFrame(() => {
      frame.style.transform = `scale(${this._zoomScale})`
    })
  }

  _onTransformTransitionEnd(event) {
    if (event.propertyName !== "transform") return
    const frame = event.currentTarget
    if (this._zoomState !== "zooming") return
    frame.removeEventListener("transitionend", this._onTransformTransitionEnd)
    this._finalizeTransformZoom(frame)
  }

  _finalizeTransformZoom(frame) {
    this._panX = 0
    this._panY = 0
    this._zoomState = "zoomed"
    this.viewport?.classList.add("is-zoomed")
    this.stage?.classList.add("is-zoomed-active")
  }

  zoomOut(frame) {
    if (this._zoomState !== "zoomed" || !this.viewport) return
    const img = frame.querySelector(".product-header-lightbox__image")
    if (!img) return

    this._zoomState = "zooming"
    this._panPointerId = null
    this.viewport.classList.remove("is-zoomed")
    this.stage?.classList.remove("is-zoomed-active")

    const finish = () => {
      this._finishZoomOut(frame, img)
    }

    const onTransformTransitionEnd = (event) => {
      if (event.propertyName !== "transform") return
      frame.removeEventListener("transitionend", onTransformTransitionEnd)
      finish()
    }

    frame.addEventListener("transitionend", onTransformTransitionEnd)
    requestAnimationFrame(() => {
      frame.style.transform = "scale(1) translate(0px, 0px)"
    })
    window.setTimeout(() => {
      if (this._zoomState === "zooming") {
        frame.removeEventListener("transitionend", onTransformTransitionEnd)
        finish()
      }
    }, 400)
  }

  _finishZoomOut(frame, img) {
    frame.style.transition = "none"
    frame.style.transform = ""
    frame.style.transformOrigin = ""
    frame.classList.remove("is-zoomed")
    frame.style.transition = ""

    this._panX = 0
    this._panY = 0
    this._zoomState = "idle"
    this._setSwiperTouchEnabled(true)
  }
}

window.ProductGalleryLightbox = ProductGalleryLightbox
window.dispatchEvent(new CustomEvent("product-gallery-lightbox:ready"))
